/* UserRemoteStorage.js */

"use strict";
const EventEmitter = require("events");
const _ = require("lodash");

/**
 * Local UserRemoteStorage class,
 * stores the local instance of the remote storage,
 * and methods to work with it.
 *
 * Should be created with UserDataStorageFactory class methods
 * getRemoteStorageInstance(storageRootName, storageKey), or
 * getRemoteStorageInstanceForEntityGrid(storageRootName, storageKey), or
 * getSessionStorageInstance(storageRootName, storageKey)
 */
class UserRemoteStorage extends EventEmitter {
    constructor(storageRootName, storageKey, userSession, logger) {
        super();

        this._storageKey = storageKey ? storageKey : "tmpSection";
        this._storageRootName = storageRootName ? storageRootName : "system";
        this._userSession = userSession;
        this._logger = logger;

        this._storageName = this.getStorageName(this._storageKey);

        this._storage = undefined;
        this._storageData = undefined;
    }

    get storageName() {
        return this._storageName;
    }

    get storage() {
        return this._storage;
    }

    set storage(newStorage) {
        this._storage = newStorage;
    }

    get storageData() {
        return this._storageData;
    }

    // eslint-disable-next-line no-unused-vars
    initialize(steve, addOnOptions) {
        this._registerListeners();
        return Promise.resolve();
    }

    _registerListeners() {}

    _unregisterListeners() {}

    shutdown() {
        //this._storage.removeItem(this._storageName);
        return new Promise((resolve) => {
            this._unregisterListeners();
            this._storageRootName = undefined;
            this._storageKey = undefined;
            this._storageName = undefined;
            this._storage = undefined;
            this._storageData = undefined;
            this._userSession = undefined;
            return resolve();
        });
    }

    getData(key, ignoreSession = false) {
        let storageData = this.getStorageData(this._storageName),
            keyData,
            sessionToken;

        if (!storageData) {
            return;
        }

        if (key) {
            keyData = storageData[key];
            if (
                this._userSession &&
                keyData &&
                Object.hasOwn(keyData, "sessionToken") &&
                ignoreSession === false
            ) {
                return keyData.sessionToken === this._userSession.token ? keyData.value : "";
            } else if (ignoreSession === true && keyData && typeof keyData.value !== "undefined") {
                return keyData.value;
            } else {
                return keyData;
            }
        } else {
            if (_.isObject(storageData) && this._userSession && ignoreSession === false) {
                Object.keys(storageData).forEach((key) => {
                    keyData = storageData[key];
                    sessionToken = _.get(keyData, "sessionToken");
                    if (sessionToken !== this._userSession.token) {
                        storageData[key] = {};
                    }
                });
            }
            return storageData;
        }
    }

    setData(data, key, force = false) {
        let keyValueData = {},
            storageName = this._storageName;

        if (key && typeof key === "string") {
            storageName = storageName + "." + key;
            if (this._userSession) {
                keyValueData = {
                    value: data,
                    sessionToken: this._userSession.token,
                };
            } else {
                keyValueData = data;
            }
        }

        const userData = force ? data : Object.assign({}, this._storageData, keyValueData);
        this._storageData[key] = userData;

        this.updateStorage(keyValueData, storageName);
    }

    removeData(key) {
        this._storage.removeItem(key);
    }

    updateStorage(keyValueData, storageName) {
        const storage = this.getStorageData(storageName);
        let data = Object.assign({}, storage, keyValueData);

        //if (typeof data !== 'string') {
        //    data = JSON.stringify(data);
        //}
        if (typeof data === "object") {
            this._storage.setItem(storageName, data);
        } else {
            this._logger.info("UserGridData: Data is not a valid object");
        }
    }

    _onStorageUpdated(eventData) {
        if (eventData.key !== this.storageName) {
            return;
        }
        let newStorage = this._storageData;
        try {
            newStorage = JSON.parse(eventData.newValue) || {};
        } catch (e) {
            this._logger.warn(
                {error: e},
                "UserRemoteStorage._onStorageUpdated() - Invalid data from the storage"
            );
        }

        this.setData(newStorage, null, true);
    }

    _checkDiff(currentStorage, newStorage) {
        const keysUpdated = [];
        if (!newStorage) {
            return keysUpdated;
        }
        Object.keys(newStorage).forEach((key) => {
            if (!Object.hasOwn(currentStorage, key)) {
                return keysUpdated.push(key);
            }
            const newValue = newStorage[key];
            if (Array.isArray(newValue)) {
                if (newValue.length !== currentStorage[key].length) {
                    return keysUpdated.push(key);
                }
                const isEq = newValue.every((val, idx) => val === currentStorage[key][idx]);
                if (!isEq) {
                    return keysUpdated.push(key);
                }
            } else if (Object(newValue) === newValue) {
                const newValueKeys = Object.keys(newValue);
                if (newValueKeys.length !== Object.keys(currentStorage[key]).length) {
                    return keysUpdated.push(key);
                }
                const isEq = newValueKeys.every(
                    (objectKey) => newValue[objectKey] === currentStorage[key][objectKey]
                );
                if (!isEq) {
                    return keysUpdated.push(key);
                }
            }
        });
        return keysUpdated;
    }

    getStorageName(key = "tmpSection") {
        return key;
    }

    getStorageData(storageName) {
        let storageData;

        try {
            storageData = this._storage.getItem(storageName) || "{}";
            if (typeof storageData === "string") {
                storageData = JSON.parse(storageData);
            }
        } catch (e) {
            this._logger.warn(
                {error: e},
                "UserRemoteStorage.getStorageData() - Can't get storage data, for storage - " +
                    storageName
            );
            storageData = {};
        }
        return storageData;
    }
}

module.exports = UserRemoteStorage;
